import { initializeApp } from "firebase/app";
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
  GoogleAuthProvider,
  onAuthStateChanged,
} from "firebase/auth";

const apiKey = process.env.REACT_APP_API_KEY;
const authDomain = process.env.REACT_APP_AUTH_DOMAIN;
const projectId = process.env.REACT_APP_PROJECT_ID;
const storageBucket = process.env.REACT_APP_STORAGE_BUCKET;
const messagingSenderId = process.env.REACT_APP_MESSAGING_SENDER_ID;
const appId = process.env.REACT_APP_APP_ID;
const measurementId = process.env.REACT_APP_MEASUREMENT_ID;

const firebaseConfig = {
  apiKey,
  authDomain,
  projectId,
  storageBucket,
  messagingSenderId,
  appId,
  measurementId,
};

const app = initializeApp(firebaseConfig);
const auth = initializeAuth(app, {
  persistence: browserLocalPersistence,
});

let authUser = null;
let authInitialized = false;
let authListeners = [];
let hadUserBefore = false;

const isLoginPage = () => {
  return window.location.pathname === '/login' || 
         window.location.pathname === '/signup' ||
         window.location.pathname === '/reset';
};

onAuthStateChanged(auth, async (user) => {
  const wasLoggedIn = hadUserBefore;
  authUser = user;
  authInitialized = true;
  
  console.log("Firebase: onAuthStateChanged fired", {
    user: user ? user.email : null,
    wasLoggedIn,
    hadUserBefore,
    isLoginPage: isLoginPage(),
    localStorageIsLoggedIn: localStorage.getItem("isLoggedIn"),
    localStorageHasToken: !!localStorage.getItem("token"),
  });
  
  if (user) {
    hadUserBefore = true;
    localStorage.setItem("isLoggedIn", "true");
    try {
      const token = await user.getIdToken();
      localStorage.setItem("token", token);
      console.log("Firebase: Token refreshed successfully, expires in ~1hr");
    } catch (e) {
      console.error("Firebase: Failed to get token on auth change:", e);
    }
  } else if (wasLoggedIn && !isLoginPage()) {
    console.log("Firebase: User logged out, clearing localStorage");
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("token");
  }
  
  authListeners.forEach(listener => listener(user));
});

let authReadyPromise = null;

export const waitForFirebaseAuth = async () => {
  if (authInitialized && authUser) {
    return authUser;
  }
  
  if (authInitialized) {
    return auth.currentUser;
  }
  
  if (isLoginPage()) {
    return null;
  }
  
  if (authReadyPromise) {
    return authReadyPromise;
  }
  
  authReadyPromise = (async () => {
    const hasStoredSession = localStorage.getItem("isLoggedIn") && localStorage.getItem("token");
    
    console.log("Firebase: waitForFirebaseAuth starting", {
      hasStoredSession,
      authInitialized,
      currentUser: auth.currentUser ? auth.currentUser.email : null,
    });
    
    try {
      await Promise.race([
        auth.authStateReady(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Auth ready timeout')), 5000))
      ]);
      console.log("Firebase: authStateReady resolved", { currentUser: auth.currentUser ? auth.currentUser.email : null });
    } catch (e) {
      console.warn("Firebase: authStateReady timeout", e.message);
    }
    
    if (auth.currentUser) {
      console.log("Firebase: Returning currentUser from authStateReady:", auth.currentUser.email);
      return auth.currentUser;
    }
    
    if (!hasStoredSession) {
      console.log("Firebase: No stored session, returning null");
      return null;
    }
    
    console.log("Firebase: Waiting for onAuthStateChanged (3s timeout)...");
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        console.warn("Firebase: onAuthStateChanged timeout - user not restored");
        resolve(null);
      }, 3000);
      
      const listener = (user) => {
        if (user) {
          clearTimeout(timeoutId);
          authListeners = authListeners.filter(l => l !== listener);
          console.log("Firebase: Got user from onAuthStateChanged:", user.email);
          resolve(user);
        }
      };
      
      authListeners.push(listener);
    });
  })();
  
  return authReadyPromise;
};

export const getCurrentUser = () => auth.currentUser;

export {
  auth,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
  GoogleAuthProvider,
};
